import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronLeft, Loader2, Building2, Users, BookOpen, GraduationCap,
  DollarSign, TrendingUp, BarChart3, Shield, ClipboardCheck, Play,
  Calendar, Route, Zap, AlertTriangle, CheckCircle2, XCircle, Edit,
  Trash2, Power, PowerOff, CreditCard, Globe, Lock, Star, Eye,
  UserPlus, Settings2, Package, FileText, Activity, ChevronRight,
  X, Search, Download, ArrowUpRight, Layers, Plus
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import api from "../../lib/api";
import { cn } from "../../lib/utils";

const PLAN_COLORS = { free: "bg-gray-100 text-gray-700", starter: "bg-blue-50 text-blue-700", professional: "bg-violet-50 text-violet-700", enterprise: "bg-amber-50 text-amber-700", basic: "bg-blue-50 text-blue-700" };
const STATUS_COLORS = { active: "bg-green-50 text-green-700 border-green-200", inactive: "bg-red-50 text-red-700 border-red-200", deleted: "bg-gray-100 text-gray-500 border-gray-200" };

const TABS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "revenue", label: "Revenue", icon: DollarSign },
  { id: "users", label: "Users", icon: Users },
  { id: "courses", label: "Courses", icon: BookOpen },
  { id: "compliance", label: "Compliance", icon: Shield },
  { id: "learning", label: "Paths", icon: Route },
  { id: "ilt", label: "ILT", icon: Play },
  { id: "ai", label: "AI Insights", icon: Zap },
  { id: "health", label: "Health", icon: Activity },
];

const TenantDetailPage = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [healthScore, setHealthScore] = useState(null);

  // Sub-data
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [compliances, setCompliances] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [learningPaths, setLearningPaths] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [subLoading, setSubLoading] = useState(false);

  // Dialogs
  const [showDelete, setShowDelete] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  // Create Compliance & Assessment dialogs
  const [showCreateCompliance, setShowCreateCompliance] = useState(false);
  const [complianceForm, setComplianceForm] = useState({ title: "", description: "", category: "safety", recurrence: "annual", is_mandatory: true, regulatory_body: "" });
  const [complianceSaving, setComplianceSaving] = useState(false);

  const [showCreateAssessment, setShowCreateAssessment] = useState(false);
  const [assessmentForm, setAssessmentForm] = useState({ title: "", description: "", passing_score: 70, time_limit_minutes: 30, questions: [] });
  const [assessmentSaving, setAssessmentSaving] = useState(false);
  const [newQuestion, setNewQuestion] = useState({ question_text: "", options: ["", "", "", ""], correct_answer: "" });

  const fetchTenant = async () => {
    try {
      const [t, a] = await Promise.all([
        api.get(`/tenants/${tenantId}`),
        api.get(`/tenants/${tenantId}/analytics`),
      ]);
      setTenant(t.data);
      setAnalytics(a.data);
    } catch { toast.error("Failed to load tenant"); navigate("/admin/tenants"); }
    finally { setLoading(false); }
  };

  const fetchSubData = async (type) => {
    setSubLoading(true);
    try {
      if (type === "users") { const r = await api.get(`/tenants/${tenantId}/users`); setUsers(r.data || []); }
      if (type === "courses") { const r = await api.get(`/tenants/${tenantId}/courses`); setCourses(r.data || []); }
      if (type === "compliance") {
        const [c, a] = await Promise.all([api.get(`/tenants/${tenantId}/compliances`), api.get(`/tenants/${tenantId}/assessments`)]);
        setCompliances(c.data || []); setAssessments(a.data || []);
      }
      if (type === "learning") { const r = await api.get(`/tenants/${tenantId}/learning-paths`); setLearningPaths(r.data || []); }
      if (type === "ilt") { const r = await api.get(`/tenants/${tenantId}/sessions`); setSessions(r.data || []); }
    } catch {}
    finally { setSubLoading(false); }
  };

  useEffect(() => { fetchTenant(); }, [tenantId]);
  useEffect(() => { if (["users", "courses", "compliance", "learning", "ilt"].includes(activeTab)) fetchSubData(activeTab); }, [activeTab]);
  useEffect(() => {
    if (activeTab === "health" && tenantId) {
      api.get(`/tenants/${tenantId}/health-score`).then(r => setHealthScore(r.data)).catch(() => {});
    }
  }, [activeTab, tenantId]);

  const handleDeactivate = async () => {
    if (!window.confirm(`Deactivate ${tenant?.name}? All users will lose access.`)) return;
    try {
      await api.post(`/tenants/${tenantId}/deactivate`);
      toast.success("Tenant deactivated successfully");
      fetchTenant();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to deactivate"); }
  };

  const handleActivate = async () => {
    if (!window.confirm(`Activate ${tenant?.name}?`)) return;
    try {
      await api.post(`/tenants/${tenantId}/activate`);
      toast.success("Tenant activated successfully");
      fetchTenant();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to activate"); }
  };

  const handleMarkFree = async () => {
    if (!window.confirm("Mark this tenant as Free tier? This will cancel any active subscriptions.")) return;
    try {
      await api.post(`/tenants/${tenantId}/mark-free`);
      toast.success("Tenant marked as free");
      fetchTenant();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to mark as free"); }
  };

  const handleConvertToPaid = async () => {
    if (!window.confirm("Convert this tenant to a Paid subscription?")) return;
    try {
      await api.post(`/admin/financial/trial/${tenantId}`, { action: "convert_to_paid" });
      toast.success("Tenant converted to paid");
      fetchTenant();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to convert to paid"); }
  };

  const handleDelete2FA = async () => {
    if (!deletePassword) { toast.error("Enter your admin password"); return; }
    setDeleting(true);
    try {
      await api.post(`/tenants/${tenantId}/delete-2fa`, { password: deletePassword });
      toast.success("Tenant deleted");
      setShowDelete(false);
      navigate("/admin/tenants");
    } catch (e) { toast.error(e.response?.data?.detail || "Deletion failed — verify password"); }
    finally { setDeleting(false); }
  };

  const handleEditSave = async () => {
    setEditSaving(true);
    try {
      await api.put(`/tenants/${tenantId}`, editForm);
      toast.success("Tenant updated");
      setShowEdit(false);
      fetchTenant();
    } catch { toast.error("Update failed"); }
    finally { setEditSaving(false); }
  };

  const openEdit = () => {
    setEditForm({ name: tenant?.name, plan: tenant?.plan, industry: tenant?.industry, primary_color: tenant?.primary_color, custom_domain: tenant?.custom_domain });
    setShowEdit(true);
  };

  const handleCreateCompliance = async () => {
    if (!complianceForm.title) { toast.error("Title is required"); return; }
    setComplianceSaving(true);
    try {
      await api.post(`/tenants/${tenantId}/compliances`, complianceForm);
      toast.success("Compliance template created");
      setShowCreateCompliance(false);
      setComplianceForm({ title: "", description: "", category: "safety", recurrence: "annual", is_mandatory: true, regulatory_body: "" });
      fetchSubData("compliance");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setComplianceSaving(false);
  };

  const handleDeleteCompliance = async (id) => {
    if (!window.confirm("Delete this compliance template?")) return;
    try { await api.delete(`/tenants/${tenantId}/compliances/${id}`); toast.success("Deleted"); fetchSubData("compliance"); } catch { toast.error("Failed"); }
  };

  const addQuestionToAssessment = () => {
    if (!newQuestion.question_text) { toast.error("Question text required"); return; }
    if (!newQuestion.correct_answer) { toast.error("Select correct answer"); return; }
    setAssessmentForm({
      ...assessmentForm,
      questions: [...assessmentForm.questions, { ...newQuestion, options: newQuestion.options.filter(o => o.trim()) }]
    });
    setNewQuestion({ question_text: "", options: ["", "", "", ""], correct_answer: "" });
  };

  const removeQuestion = (idx) => {
    setAssessmentForm({ ...assessmentForm, questions: assessmentForm.questions.filter((_, i) => i !== idx) });
  };

  const handleCreateAssessment = async () => {
    if (!assessmentForm.title) { toast.error("Title is required"); return; }
    if (assessmentForm.questions.length === 0) { toast.error("Add at least one question"); return; }
    setAssessmentSaving(true);
    try {
      await api.post(`/tenants/${tenantId}/assessments`, assessmentForm);
      toast.success("Assessment created");
      setShowCreateAssessment(false);
      setAssessmentForm({ title: "", description: "", passing_score: 70, time_limit_minutes: 30, questions: [] });
      fetchSubData("compliance");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setAssessmentSaving(false);
  };

  const handleDeleteAssessment = async (id) => {
    if (!window.confirm("Delete this assessment?")) return;
    try { await api.delete(`/tenants/${tenantId}/assessments/${id}`); toast.success("Deleted"); fetchSubData("compliance"); } catch { toast.error("Failed"); }
  };

  if (loading) return <AdminLayout title="Tenant"><div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></AdminLayout>;
  if (!tenant) return null;

  const ov = analytics?.overview || {};
  const rev = analytics?.revenue || {};

  return (
    <AdminLayout title={tenant.name}>
      <div className="space-y-5 max-w-7xl" data-testid="tenant-detail-page">
        {/* Back + Header */}
        <Button variant="ghost" size="sm" className="gap-1 text-xs -ml-2" onClick={() => navigate("/admin/tenants")} data-testid="back-to-tenants"><ChevronLeft className="w-4 h-4" /> All Tenants</Button>

        <Card className="border shadow-none overflow-hidden" data-testid="tenant-header">
          <CardContent className="p-5">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl flex items-center justify-center font-bold text-xl text-white" style={{ backgroundColor: tenant.primary_color || "#1B4F8A" }}>
                  {tenant.name?.charAt(0)?.toUpperCase()}
                </div>
                <div>
                  <h1 className="text-xl font-bold">{tenant.name}</h1>
                  <p className="text-xs text-muted-foreground">{tenant.subdomain}.odesie.com &middot; {tenant.industry}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Badge className={cn("text-[10px] px-2 py-0.5 border", STATUS_COLORS[tenant.status])}>{tenant.status}</Badge>
                    <Badge className={cn("text-[10px] px-2 py-0.5", PLAN_COLORS[tenant.plan])}>{tenant.plan}</Badge>
                    {rev.tier && <Badge variant="outline" className="text-[10px] capitalize">{rev.tier} License</Badge>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={openEdit} data-testid="edit-tenant-btn"><Edit className="w-3 h-3" /> Edit</Button>
                {tenant.status === "active" ? (
                  <Button variant="outline" size="sm" className="h-8 text-xs gap-1 text-amber-600 border-amber-300 hover:bg-amber-50" onClick={handleDeactivate} data-testid="deactivate-btn"><PowerOff className="w-3 h-3" /> Deactivate</Button>
                ) : tenant.status !== "deleted" && (
                  <Button variant="outline" size="sm" className="h-8 text-xs gap-1 text-green-600 border-green-300 hover:bg-green-50" onClick={handleActivate} data-testid="activate-btn"><Power className="w-3 h-3" /> Activate</Button>
                )}
                {tenant.plan !== "free" && tenant.billing_status !== "free" && (
                  <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={handleMarkFree} data-testid="mark-free-btn"><CreditCard className="w-3 h-3" /> Mark Free</Button>
                )}
                {(tenant.plan === "free" || tenant.billing_status === "free") && (
                  <Button size="sm" className="h-8 text-xs gap-1" onClick={handleConvertToPaid} data-testid="convert-paid-btn"><DollarSign className="w-3 h-3" /> Convert to Paid</Button>
                )}
                {tenant.status !== "deleted" && (
                  <Button variant="outline" size="sm" className="h-8 text-xs gap-1 text-red-600 border-red-300 hover:bg-red-50" onClick={() => setShowDelete(true)} data-testid="delete-tenant-btn"><Trash2 className="w-3 h-3" /> Delete (2FA)</Button>
                )}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mt-5 pt-4 border-t">
              {[
                { l: "Users", v: ov.users, icon: Users },
                { l: "Courses", v: ov.courses, icon: BookOpen },
                { l: "Enrollments", v: ov.enrollments, icon: GraduationCap },
                { l: "Completion", v: `${ov.completion_rate || 0}%`, icon: CheckCircle2 },
                { l: "MRR", v: `$${rev.mrr || 0}`, icon: DollarSign },
                { l: "ARR", v: `$${(rev.arr || 0).toLocaleString()}`, icon: TrendingUp },
              ].map((s, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <s.icon className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div><p className="text-base font-bold leading-none">{s.v}</p><p className="text-[9px] text-muted-foreground">{s.l}</p></div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={cn("px-3 py-2 text-xs font-medium border-b-2 -mb-px whitespace-nowrap transition-colors flex items-center gap-1.5", activeTab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground")}
              data-testid={`tenant-tab-${t.id}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {/* ═══ OVERVIEW ═══ */}
        {activeTab === "overview" && analytics && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
              {[
                { l: "Active Users", v: ov.active_users, icon: Activity, c: "text-green-600 bg-green-50" },
                { l: "ILT Sessions", v: ov.sessions, icon: Play, c: "text-violet-600 bg-violet-50" },
                { l: "Assessments", v: ov.assessments, icon: ClipboardCheck, c: "text-amber-600 bg-amber-50" },
                { l: "Compliances", v: ov.compliances, icon: Shield, c: "text-blue-600 bg-blue-50" },
                { l: "Learning Paths", v: ov.learning_paths, icon: Route, c: "text-primary bg-primary/10" },
              ].map((s, i) => (
                <Card key={i} className="border shadow-none"><CardContent className="p-3 flex items-center gap-2.5">
                  <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center shrink-0", s.c)}><s.icon className="w-4 h-4" /></div>
                  <div><p className="text-lg font-bold leading-none">{s.v}</p><p className="text-[10px] text-muted-foreground">{s.l}</p></div>
                </CardContent></Card>
              ))}
            </div>

            {/* Enrollment Trend */}
            <Card className="border shadow-none">
              <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Enrollment Trend (6 Months)</CardTitle></CardHeader>
              <CardContent className="px-5 pb-5">
                <div className="flex items-end gap-3 h-32">
                  {analytics.enrollment_trend?.map((d, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full flex flex-col items-center gap-0.5" style={{ height: "100px" }}>
                        <div className="w-full bg-primary/20 rounded-t" style={{ height: `${Math.max((d.enrollments / Math.max(...analytics.enrollment_trend.map(x => x.enrollments), 1)) * 80, 4)}px` }} />
                        <div className="w-full bg-green-400/50 rounded-b" style={{ height: `${Math.max((d.completions / Math.max(...analytics.enrollment_trend.map(x => x.enrollments), 1)) * 80, 2)}px` }} />
                      </div>
                      <span className="text-[8px] text-muted-foreground">{d.month?.slice(5)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-4 mt-3 text-[9px] text-muted-foreground">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-primary/30" /> Enrollments</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400/50" /> Completions</span>
                </div>
              </CardContent>
            </Card>

            {/* Features */}
            <Card className="border shadow-none">
              <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Enabled Features ({analytics.enabled_features?.length || 0})</CardTitle></CardHeader>
              <CardContent className="px-5 pb-5 flex flex-wrap gap-1.5">
                {analytics.enabled_features?.length > 0 ? analytics.enabled_features.map(f => (
                  <Badge key={f} className="text-[9px] bg-green-50 text-green-700 border-green-200 gap-0.5"><CheckCircle2 className="w-2.5 h-2.5" />{f.replace(/_/g, " ")}</Badge>
                )) : <p className="text-xs text-muted-foreground">No custom feature flags — using defaults</p>}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ═══ REVENUE & PROJECTIONS ═══ */}
        {activeTab === "revenue" && analytics && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { l: "Monthly Revenue", v: `$${rev.mrr}`, icon: DollarSign },
                { l: "Annual Revenue", v: `$${rev.arr?.toLocaleString()}`, icon: TrendingUp },
                { l: "Per User Price", v: `$${rev.per_user_price}/mo`, icon: Users },
                { l: "License Tier", v: rev.tier, icon: CreditCard },
              ].map((s, i) => (
                <Card key={i} className="border shadow-none"><CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><s.icon className="w-5 h-5 text-primary" /></div>
                  <div><p className="text-lg font-bold">{s.v}</p><p className="text-[10px] text-muted-foreground">{s.l}</p></div>
                </CardContent></Card>
              ))}
            </div>

            <Card className="border shadow-none">
              <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">3-Year Revenue Projection</CardTitle></CardHeader>
              <CardContent className="px-5 pb-5">
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                    <span className="text-sm font-medium">Current (2026)</span>
                    <span className="text-lg font-bold text-primary">${rev.arr?.toLocaleString()}</span>
                  </div>
                  {analytics.projections?.map(p => (
                    <div key={p.year} className="flex items-center justify-between p-3 rounded-lg border">
                      <div><span className="text-sm font-medium">{p.year}</span><span className="text-[10px] text-muted-foreground ml-2">({(p.growth_rate * 100).toFixed(0)}% growth)</span></div>
                      <span className="text-lg font-bold">${Math.round(p.projected_arr).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ═══ USERS ═══ */}
        {activeTab === "users" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{users.length} users in this tenant</p>
            </div>
            {subLoading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
              <Card className="border shadow-none overflow-hidden" data-testid="tenant-users-table">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr className="border-b bg-muted/30">
                      <th className="p-3 text-left text-[10px] font-semibold uppercase text-muted-foreground">User</th>
                      <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Role</th>
                      <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Status</th>
                      <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Joined</th>
                    </tr></thead>
                    <tbody>
                      {users.map(u => (
                        <tr key={u.id} className="border-b hover:bg-muted/20">
                          <td className="p-3"><p className="text-sm font-medium">{u.full_name}</p><p className="text-[10px] text-muted-foreground">{u.email}</p></td>
                          <td className="p-3 text-center"><Badge variant="outline" className="text-[9px] capitalize">{u.role?.replace("_", " ")}</Badge></td>
                          <td className="p-3 text-center"><Badge className={cn("text-[9px] border", u.is_active !== false ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-600 border-red-200")}>{u.is_active !== false ? "Active" : "Inactive"}</Badge></td>
                          <td className="p-3 text-center text-xs text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* ═══ COURSES ═══ */}
        {activeTab === "courses" && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{courses.length} courses</p>
            {subLoading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
              <Card className="border shadow-none overflow-hidden" data-testid="tenant-courses-table">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr className="border-b bg-muted/30">
                      <th className="p-3 text-left text-[10px] font-semibold uppercase text-muted-foreground">Course</th>
                      <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Type</th>
                      <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Access</th>
                      <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Published</th>
                    </tr></thead>
                    <tbody>
                      {courses.map(c => (
                        <tr key={c.id} className="border-b hover:bg-muted/20 cursor-pointer" onClick={() => navigate(`/admin/courses/${c.id}`)}>
                          <td className="p-3"><p className="text-sm font-medium">{c.title}</p><p className="text-[10px] text-muted-foreground">{c.category}</p></td>
                          <td className="p-3 text-center"><Badge variant="outline" className="text-[9px]">{c.course_type}</Badge></td>
                          <td className="p-3 text-center"><Badge variant="outline" className="text-[9px] capitalize">{c.access_model || "subscription"}</Badge></td>
                          <td className="p-3 text-center">{c.is_published ? <CheckCircle2 className="w-4 h-4 text-green-500 mx-auto" /> : <XCircle className="w-4 h-4 text-muted-foreground/30 mx-auto" />}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* ═══ COMPLIANCE & ASSESSMENTS ═══ */}
        {activeTab === "compliance" && (
          <div className="space-y-5">
            {subLoading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
              <>
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold flex items-center gap-1.5"><Shield className="w-4 h-4" /> Compliance Templates ({compliances.length})</h3>
                    <Button size="sm" className="text-xs h-7 gap-1" onClick={() => setShowCreateCompliance(true)} data-testid="create-compliance-btn"><Plus className="w-3 h-3" /> Create Compliance</Button>
                  </div>
                  {compliances.length === 0 ? <Card className="border shadow-none"><CardContent className="py-8 text-center text-sm text-muted-foreground">No compliance templates</CardContent></Card> : (
                    <div className="space-y-2">{compliances.map(c => (
                      <Card key={c.id} className="border shadow-none"><CardContent className="p-3 flex items-center justify-between">
                        <div><p className="text-sm font-medium">{c.title}</p><p className="text-[10px] text-muted-foreground">{c.category} &middot; {c.recurrence || "N/A"}</p></div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[9px]">{c.is_mandatory ? "Mandatory" : "Optional"}</Badge>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteCompliance(c.id)} data-testid={`delete-compliance-${c.id}`}><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      </CardContent></Card>
                    ))}</div>
                  )}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold flex items-center gap-1.5"><ClipboardCheck className="w-4 h-4" /> Assessments ({assessments.length})</h3>
                    <Button size="sm" className="text-xs h-7 gap-1" onClick={() => setShowCreateAssessment(true)} data-testid="create-assessment-btn"><Plus className="w-3 h-3" /> Create Assessment</Button>
                  </div>
                  {assessments.length === 0 ? <Card className="border shadow-none"><CardContent className="py-8 text-center text-sm text-muted-foreground">No assessments</CardContent></Card> : (
                    <div className="space-y-2">{assessments.map(a => (
                      <Card key={a.id} className="border shadow-none"><CardContent className="p-3 flex items-center justify-between">
                        <div><p className="text-sm font-medium">{a.title}</p><p className="text-[10px] text-muted-foreground">{a.questions?.length || 0} questions &middot; Pass: {a.passing_score}%</p></div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[9px]">{a.is_published ? "Published" : "Draft"}</Badge>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteAssessment(a.id)} data-testid={`delete-assessment-${a.id}`}><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      </CardContent></Card>
                    ))}</div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ═══ LEARNING PATHS ═══ */}
        {activeTab === "learning" && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{learningPaths.length} learning paths</p>
            {subLoading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
              learningPaths.length === 0 ? <Card className="border shadow-none"><CardContent className="py-12 text-center text-sm text-muted-foreground">No learning paths configured</CardContent></Card> : (
                <div className="space-y-2">{learningPaths.map(lp => (
                  <Card key={lp.id} className="border shadow-none"><CardContent className="p-3 flex items-center justify-between">
                    <div><p className="text-sm font-medium">{lp.title}</p><p className="text-[10px] text-muted-foreground">{lp.courses?.length || 0} courses &middot; {lp.difficulty_level || "mixed"}</p></div>
                    <Badge variant="outline" className="text-[9px]">{lp.is_published ? "Published" : "Draft"}</Badge>
                  </CardContent></Card>
                ))}</div>
              )
            )}
          </div>
        )}

        {/* ═══ ILT SESSIONS ═══ */}
        {activeTab === "ilt" && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{sessions.length} sessions</p>
            {subLoading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
              sessions.length === 0 ? <Card className="border shadow-none"><CardContent className="py-12 text-center text-sm text-muted-foreground">No ILT sessions</CardContent></Card> : (
                <Card className="border shadow-none overflow-hidden"><div className="overflow-x-auto">
                  <table className="w-full"><thead><tr className="border-b bg-muted/30">
                    <th className="p-3 text-left text-[10px] font-semibold uppercase text-muted-foreground">Session</th>
                    <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Status</th>
                    <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Date</th>
                    <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Capacity</th>
                  </tr></thead><tbody>
                    {sessions.map(s => (
                      <tr key={s.id} className="border-b hover:bg-muted/20">
                        <td className="p-3"><p className="text-sm font-medium">{s.title}</p><p className="text-[10px] text-muted-foreground">{s.instructor_name} &middot; {s.location}</p></td>
                        <td className="p-3 text-center"><Badge variant="outline" className="text-[9px] capitalize">{s.status}</Badge></td>
                        <td className="p-3 text-center text-xs text-muted-foreground">{s.session_date || "-"}</td>
                        <td className="p-3 text-center text-xs">{s.registrations?.length || 0}/{s.capacity}</td>
                      </tr>
                    ))}
                  </tbody></table>
                </div></Card>
              )
            )}
          </div>
        )}

        {/* ═══ AI INSIGHTS & UPSELLS ═══ */}
        {activeTab === "ai" && analytics && (
          <div className="space-y-5">
            {/* Sales Intel */}
            {analytics.sales_intel?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Sales Intelligence</h3>
                <div className="space-y-2">
                  {analytics.sales_intel.map((s, i) => (
                    <Card key={i} className={cn("border shadow-none", s.severity === "warning" ? "border-amber-200 bg-amber-50/30" : s.severity === "opportunity" ? "border-green-200 bg-green-50/30" : "")}>
                      <CardContent className="p-4"><p className="text-sm font-medium">{s.area}</p><p className="text-xs text-muted-foreground mt-1">{s.detail}</p></CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Upsells */}
            {analytics.upsells?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5"><ArrowUpRight className="w-4 h-4" /> Upsell & Cross-Sell Opportunities</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {analytics.upsells.map((u, i) => (
                    <Card key={i} className="border shadow-none hover:border-primary/30 transition-colors">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <Badge className={cn("text-[9px] px-1.5 py-0 border", u.type === "upsell" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-green-50 text-green-700 border-green-200")}>{u.type}</Badge>
                          <Badge variant="outline" className="text-[9px] capitalize">{u.priority}</Badge>
                        </div>
                        <p className="text-sm font-medium">{u.title}</p>
                        <p className="text-xs text-muted-foreground mt-1">{u.description}</p>
                        {u.potential_revenue > 0 && <p className="text-sm font-bold text-green-600 mt-2">+${u.potential_revenue.toLocaleString()}/yr potential</p>}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* AI Recommendations */}
            {analytics.ai_recommendations?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5"><Zap className="w-4 h-4" /> AI-Suggested Actions</h3>
                <div className="space-y-2">
                  {analytics.ai_recommendations.map((r, i) => (
                    <Card key={i} className="border shadow-none">
                      <CardContent className="p-4 flex items-start gap-3">
                        <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center shrink-0", r.category === "mandatory" ? "bg-red-50 text-red-600" : "bg-blue-50 text-blue-600")}>
                          <Zap className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium">{r.title}</p>
                            <Badge className={cn("text-[8px] px-1 py-0 border", r.category === "mandatory" ? "bg-red-50 text-red-600 border-red-200" : "bg-blue-50 text-blue-600 border-blue-200")}>{r.category}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{r.reason}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══ HEALTH SCORE ═══ */}
        {activeTab === "health" && (
          <div className="space-y-5" data-testid="tenant-health-tab">
            {healthScore ? (
              <>
                {/* Score Gauge */}
                <Card className="border shadow-none overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-8">
                      <div className="relative w-32 h-32 flex-shrink-0">
                        <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                          <circle cx="60" cy="60" r="50" fill="none" stroke="#e5e7eb" strokeWidth="10" />
                          <circle cx="60" cy="60" r="50" fill="none" strokeWidth="10" strokeLinecap="round"
                            stroke={healthScore.health_score >= 75 ? "#0E8A55" : healthScore.health_score >= 50 ? "#D97706" : "#C7322A"}
                            strokeDasharray={`${healthScore.health_score * 3.14} 314`} />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className="text-3xl font-extrabold" style={{ fontFamily: "'Sora', sans-serif" }}>{healthScore.health_score}</span>
                          <span className="text-xs text-muted-foreground">/ 100</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`text-3xl font-extrabold ${healthScore.grade === 'A' ? 'text-green-600' : healthScore.grade === 'B' ? 'text-blue-600' : healthScore.grade === 'C' ? 'text-yellow-600' : 'text-red-600'}`}
                            style={{ fontFamily: "'Sora', sans-serif" }}>Grade: {healthScore.grade}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">{healthScore.recommendation}</p>
                        <div className="flex gap-4 mt-3 text-xs">
                          <span><strong>{healthScore.total_users}</strong> Active Users</span>
                          <span><strong>{healthScore.total_enrollments}</strong> Enrollments</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Factor Breakdown */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Object.values(healthScore.factors || {}).map((f, i) => {
                    const color = f.score >= 75 ? "#0E8A55" : f.score >= 50 ? "#D97706" : "#C7322A";
                    return (
                      <Card key={i} className="border shadow-none" data-testid={`health-factor-${i}`}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-sm font-medium">{f.label}</p>
                            <span className="text-xs text-muted-foreground">{f.weight}% weight</span>
                          </div>
                          <p className="text-2xl font-extrabold" style={{ color, fontFamily: "'Sora', sans-serif" }}>{f.score}%</p>
                          <div className="w-full bg-muted rounded-full h-1.5 mt-2">
                            <div className="h-1.5 rounded-full transition-all" style={{ width: `${f.score}%`, backgroundColor: color }} />
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
            )}
          </div>
        )}
      </div>

      {/* ── DELETE 2FA DIALOG ── */}
      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-base text-red-600">Delete Tenant — 2FA Required</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Card className="border-red-200 bg-red-50/30 border shadow-none"><CardContent className="p-3">
              <p className="text-xs text-red-700"><AlertTriangle className="w-3.5 h-3.5 inline mr-1" />This will soft-delete <strong>{tenant.name}</strong> and suspend all its users. This action is logged.</p>
            </CardContent></Card>
            <div><Label className="text-xs">Enter your admin password to confirm</Label><Input type="password" value={deletePassword} onChange={e => setDeletePassword(e.target.value)} className="h-9 text-sm" placeholder="Your password" data-testid="delete-password-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setShowDelete(false)}>Cancel</Button>
            <Button size="sm" className="text-xs gap-1 bg-red-600 hover:bg-red-700" onClick={handleDelete2FA} disabled={deleting} data-testid="confirm-delete-btn">
              {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />} Delete Tenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── EDIT DIALOG ── */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-base">Edit Tenant</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input value={editForm.name || ""} onChange={e => setEditForm({...editForm, name: e.target.value})} className="h-8 text-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Plan</Label>
                <Select value={editForm.plan || "starter"} onValueChange={v => setEditForm({...editForm, plan: v})}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="free">Free</SelectItem><SelectItem value="starter">Starter</SelectItem><SelectItem value="professional">Professional</SelectItem><SelectItem value="enterprise">Enterprise</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Industry</Label><Input value={editForm.industry || ""} onChange={e => setEditForm({...editForm, industry: e.target.value})} className="h-8 text-sm" /></div>
            </div>
            <div><Label className="text-xs">Custom Domain</Label><Input value={editForm.custom_domain || ""} onChange={e => setEditForm({...editForm, custom_domain: e.target.value})} className="h-8 text-sm" placeholder="learn.company.com" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setShowEdit(false)}>Cancel</Button>
            <Button size="sm" className="text-xs gap-1" onClick={handleEditSave} disabled={editSaving}><CheckCircle2 className="w-3 h-3" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── CREATE COMPLIANCE DIALOG ── */}
      <Dialog open={showCreateCompliance} onOpenChange={setShowCreateCompliance}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-base">Create Compliance Template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Title *</Label><Input value={complianceForm.title} onChange={e => setComplianceForm({...complianceForm, title: e.target.value})} className="h-8 text-sm" placeholder="OSHA Forklift Safety" data-testid="compliance-title-input" /></div>
            <div><Label className="text-xs">Description</Label><Textarea value={complianceForm.description} onChange={e => setComplianceForm({...complianceForm, description: e.target.value})} rows={2} className="text-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Category</Label>
                <Select value={complianceForm.category} onValueChange={v => setComplianceForm({...complianceForm, category: v})}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="safety" className="text-xs">Safety</SelectItem>
                    <SelectItem value="environmental" className="text-xs">Environmental</SelectItem>
                    <SelectItem value="quality" className="text-xs">Quality</SelectItem>
                    <SelectItem value="regulatory" className="text-xs">Regulatory</SelectItem>
                    <SelectItem value="hr" className="text-xs">HR</SelectItem>
                    <SelectItem value="general" className="text-xs">General</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Recurrence</Label>
                <Select value={complianceForm.recurrence} onValueChange={v => setComplianceForm({...complianceForm, recurrence: v})}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="annual" className="text-xs">Annual</SelectItem>
                    <SelectItem value="semi_annual" className="text-xs">Semi-Annual</SelectItem>
                    <SelectItem value="quarterly" className="text-xs">Quarterly</SelectItem>
                    <SelectItem value="monthly" className="text-xs">Monthly</SelectItem>
                    <SelectItem value="one_time" className="text-xs">One-Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Regulatory Body</Label><Input value={complianceForm.regulatory_body} onChange={e => setComplianceForm({...complianceForm, regulatory_body: e.target.value})} className="h-8 text-sm" placeholder="OSHA, EPA..." /></div>
              <div className="flex items-end pb-0.5">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={complianceForm.is_mandatory} onChange={e => setComplianceForm({...complianceForm, is_mandatory: e.target.checked})} className="rounded w-4 h-4" />
                  <span className="text-xs font-medium">Mandatory</span>
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setShowCreateCompliance(false)}>Cancel</Button>
            <Button size="sm" className="text-xs gap-1" onClick={handleCreateCompliance} disabled={complianceSaving} data-testid="save-compliance-btn">
              {complianceSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── CREATE ASSESSMENT DIALOG ── */}
      <Dialog open={showCreateAssessment} onOpenChange={setShowCreateAssessment}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-base">Create Assessment</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Title *</Label><Input value={assessmentForm.title} onChange={e => setAssessmentForm({...assessmentForm, title: e.target.value})} className="h-8 text-sm" placeholder="Safety Knowledge Test" data-testid="assessment-title-input" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Pass Score %</Label><Input type="number" value={assessmentForm.passing_score} onChange={e => setAssessmentForm({...assessmentForm, passing_score: parseInt(e.target.value)||70})} className="h-8 text-sm" /></div>
                <div><Label className="text-xs">Time (min)</Label><Input type="number" value={assessmentForm.time_limit_minutes} onChange={e => setAssessmentForm({...assessmentForm, time_limit_minutes: parseInt(e.target.value)||30})} className="h-8 text-sm" /></div>
              </div>
            </div>
            <div><Label className="text-xs">Description</Label><Textarea value={assessmentForm.description} onChange={e => setAssessmentForm({...assessmentForm, description: e.target.value})} rows={2} className="text-sm" /></div>

            {/* Questions list */}
            {assessmentForm.questions.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold">Questions ({assessmentForm.questions.length})</Label>
                {assessmentForm.questions.map((q, i) => (
                  <div key={i} className="p-2.5 rounded-lg border bg-muted/10">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <p className="text-xs font-medium">Q{i + 1}: {q.question_text}</p>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {q.options.map((o, oi) => (
                            <Badge key={oi} variant="outline" className={cn("text-[9px]", o === q.correct_answer ? "bg-green-50 text-green-700 border-green-200" : "")}>{o}{o === q.correct_answer ? " ✓" : ""}</Badge>
                          ))}
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500 shrink-0" onClick={() => removeQuestion(i)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add question builder */}
            <Card className="border shadow-none">
              <CardContent className="p-3 space-y-2">
                <Label className="text-xs font-semibold">Add Question</Label>
                <Input value={newQuestion.question_text} onChange={e => setNewQuestion({...newQuestion, question_text: e.target.value})} className="h-8 text-sm" placeholder="What is the correct procedure for..." data-testid="question-text-input" />
                <div className="grid grid-cols-2 gap-2">
                  {newQuestion.options.map((opt, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <input type="radio" name="correct" checked={newQuestion.correct_answer === opt && opt !== ""} onChange={() => setNewQuestion({...newQuestion, correct_answer: opt})} className="w-3.5 h-3.5" />
                      <Input value={opt} onChange={e => { const opts = [...newQuestion.options]; opts[i] = e.target.value; setNewQuestion({...newQuestion, options: opts}); }} className="h-7 text-xs flex-1" placeholder={`Option ${String.fromCharCode(65 + i)}`} />
                    </div>
                  ))}
                </div>
                <Button size="sm" variant="outline" className="text-xs h-7 gap-1 w-full" onClick={addQuestionToAssessment} data-testid="add-question-btn"><Plus className="w-3 h-3" /> Add Question</Button>
              </CardContent>
            </Card>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setShowCreateAssessment(false)}>Cancel</Button>
            <Button size="sm" className="text-xs gap-1" onClick={handleCreateAssessment} disabled={assessmentSaving} data-testid="save-assessment-btn">
              {assessmentSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Create Assessment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default TenantDetailPage;
